import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// date-fns

// Material
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';

// CSVLink
import { CSVLink } from 'react-csv';

// Helpers
import { formatDateAndTime } from 'helpers/date';
import { getComparator, stableSort } from 'helpers/tableSortLabels';
import { currencyFormat } from 'helpers/currencyFormat';

// Actions
import * as ReportActions from 'store/ducks/reports/actions';
import { setOpen } from 'store/ducks/dialogs/actions';

// Types
import { ApplicationState } from 'store';
import { PaymentRequestInterface } from 'store/ducks/reports/types';

// Helpers

// Components
import {
  MainContent,
  XlsHeaderInterface,
  XlsRowInterface,
  DialogueExport,
  SearchComponent,
  ReportTitle,
} from 'components';
import ContentPlaceholder from 'components/TablePlaceholder';
import { TabPanel } from 'components/TabPanel';

// Constants
import CONST_PAYMENT_NEW from 'constants/payments';
import { REPORTS_TYPES } from 'constants/report';
import ITEMS from 'constants/itemsByFilter';
import TITLES_OPTIONS from 'constants/infoModulesTitle';
import { Submissions, History } from '../../../../components';
import TablePaymentRequest from './components/TablePaymentRequest';

// Styles
import useStyles from './PaymentRequestDatails.styles';

// Lazy Components
const Filter = lazy(() => import('components/Filter'));
const TotalCountComponent = lazy(() =>
  import('components/TotalCountComponent'),
);

interface PaymentRequestDatailsInterface {
  setSubmissionData: (param: any) => void;
  setIsAddSubmissiion: (param: any) => void;
  setIsEditSubmissiion: (param: any) => void;
}

const PaymentRequestDatails: React.FC<PaymentRequestDatailsInterface> = ({
  setSubmissionData,
  setIsAddSubmissiion,
  setIsEditSubmissiion,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const csvRef = useRef(null);

  const [periodString, setPeriodString] = useState('');
  const [xlsHeader, setXlsHeader] = useState<XlsHeaderInterface[]>();
  const [xlsRows, setXlsRows] = useState<Array<XlsRowInterface[]>>([]);

  const [simplePdfData, setSimplePdfData] = useState<{
    title: string;
    subtitle: string;
    filters: string;
    headers: any;
    rows: any;
  }>({
    title: '',
    subtitle: '',
    filters: '',
    headers: [],
    rows: [],
  });

  const initialPaymentRequestList = useSelector(
    (state: ApplicationState) => state.reports.paymentRequestList,
  );

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  const [tableReference, setTableReference] = useState();
  const [contentIsVisible, setContentIsVisible] = useState(false);
  const [tableCSV, setTableCSV] = useState('');
  const [generalNavGuide, setGeneralNavGuide] = useState(0);
  const [paymentRequestList, setPaymentRequestList] = useState<
    PaymentRequestInterface[]
  >([]);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setGeneralNavGuide(newValue);
  };

  const handleLoadData = useCallback(() => {
    setContentIsVisible(true);

    dispatch(
      ReportActions.paymentRequestListRequest({
        startDate: new Date().setHours(0, 0, 0),
        endDate: new Date().setHours(23, 59, 59),
      }),
    );

    const tempPeriodString = `Dia: ${new Date().toLocaleDateString('pt-BR', {
      dateStyle: 'short',
    })}`;

    setPeriodString(tempPeriodString);
  }, [dispatch]);

  const handleApply = useCallback(
    (data: any) => {
      const {
        startedAt,
        startDate,
        endDate,
        selectedSellingTerminal,
        selectedPaymentType,
        selectedPaymentStatus,
      } = data;
      setContentIsVisible(true);

      const isPeriod = startDate?.getTime() < endDate?.getTime();
      let startDateValue;
      let endDateValue;
      if (isPeriod) {
        startDateValue = new Date(startDate).getTime();
        endDateValue = new Date(endDate).getTime();
      } else if (startedAt) {
        startDateValue = new Date(startedAt).setHours(0, 0, 0);
        endDateValue = new Date(startedAt).setHours(23, 59, 59);
      }
      if (isPeriod) {
        dispatch(
          ReportActions.setSelectedStartDate({
            selectedStartDate: startDate,
          }),
        );
        dispatch(
          ReportActions.setSelectedEndDate({
            selectedEndDate: endDate,
          }),
        );
      }

      dispatch(
        ReportActions.paymentRequestListRequest({
          startDate: startDateValue,
          endDate: endDateValue,
          paymentType:
            selectedPaymentType.length > 0
              ? selectedPaymentType.map((e: any) => e.KEY).join()
              : undefined,
          sellingTerminalId:
            selectedSellingTerminal.length > 0
              ? selectedSellingTerminal.map((e: any) => e.id).join()
              : undefined,
          status:
            selectedPaymentStatus.length > 0
              ? selectedPaymentStatus.map((e: any) => e.key).join()
              : undefined,
        }),
      );

      const tempPeriodString = `De: ${
        isPeriod
          ? formatDateAndTime(new Date(startDate))
          : formatDateAndTime(new Date(startedAt))
      } até: ${
        isPeriod
          ? formatDateAndTime(new Date(endDate))
          : formatDateAndTime(new Date(startedAt))
      }`;

      let updatedPeriodString = tempPeriodString;

      if (selectedPaymentType?.length > 0) {
        const tempLinesString = `Forma de Recebimento: ${selectedPaymentType
          .map((e: any) => e.VALUE)
          .join(', ')}`;
        updatedPeriodString += ', ' + tempLinesString;
      }

      if (selectedSellingTerminal?.length > 0) {
        const tempLinesString = `Rede de Venda: ${selectedSellingTerminal
          .map((e: any) => e.id)
          .join(', ')}`;
        updatedPeriodString += ', ' + tempLinesString;
      }

      if (selectedPaymentStatus?.length > 0) {
        const tempLinesString = `Status do Pagamento: ${selectedPaymentStatus
          .map((e: any) => e.id)
          .join(', ')}`;
        updatedPeriodString += ', ' + tempLinesString;
      }

      setPeriodString(updatedPeriodString);
    },
    [dispatch],
  );

  useEffect(() => {
    setPaymentRequestList(initialPaymentRequestList);
  }, [initialPaymentRequestList]);
  // generating the table csv
  useEffect(() => {
    let csv = '';
    let header = `ID ;Valor ;Status ;Data ;Terminal ;Pagamento `;
    if (transportOperatorSelected.type === 1) {
      header += ';Operação\n';
    } else {
      header += '\n';
    }
    csv += header;
    csv += stableSort(paymentRequestList, getComparator('desc', 'createdAt'))
      .map((paymentRequest) => {
        const paymentType =
          paymentRequest.type &&
          CONST_PAYMENT_NEW.TYPES.find(({ KEY }) => KEY === paymentRequest.type)
            ?.VALUE;
        return `${paymentRequest.id.split('-')[0]};${currencyFormat({
          value: Number(paymentRequest.value) || 0,
        })};${
          paymentRequest.status
            ? CONST_PAYMENT_NEW.STATUS.find(
                ({ KEY }) => KEY === paymentRequest.status,
              )?.VALUE || '---'
            : '-'
        };${new Date(paymentRequest.createdAt).toLocaleDateString('pt-BR') ||
          '---'};${paymentRequest.name || '---'};${
          transportOperatorSelected.type === 1
            ? transportOperatorsList.find(
                (e) => e.id === paymentRequest.transportOperatorId,
              )?.name || ''
            : ''
        }${
          paymentRequest.type
            ? CONST_PAYMENT_NEW.TYPES.find(
                ({ KEY }) => KEY === paymentRequest.type,
              )?.VALUE
            : '---'
        }`;
      })
      .join('\n');
    setTableCSV(csv);
  }, [paymentRequestList]);

  useEffect(() => {
    const headerSimplePdf = [
      'ID',
      'Valor',
      'Status',
      'Data',
      'Terminal',
      'Pagamento',
    ];

    const rowsSimplePdf =
      paymentRequestList?.length > 0
        ? stableSort(
            paymentRequestList,
            getComparator('desc', 'createdAt'),
          )?.map((item: any) => [
            item?.id ? item?.id?.split('-')?.[0] : '-',
            item?.value
              ? `R$ ${item?.value?.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                })}`
              : '-',
            item?.status
              ? CONST_PAYMENT_NEW.STATUS.find(({ KEY }) => KEY === item.status)
                  ?.VALUE
              : '-',
            item?.createdAt
              ? new Date(item.createdAt).toLocaleString('pt-BR')
              : '-',
            item?.name ? item?.name : '-',
            item?.type
              ? CONST_PAYMENT_NEW.TYPES.find(({ KEY }) => KEY === item.type)
                  ?.VALUE
              : '-',
          ])
        : [];

    const tempSimplePdfData = {
      title: 'ONBOARD',
      subtitle: 'Relatório de Requisição de Pagamento',
      filters: periodString || '',
      headers: headerSimplePdf,
      rows: rowsSimplePdf,
    };

    setSimplePdfData(tempSimplePdfData);
  }, [paymentRequestList, periodString]);

  useEffect(() => {
    const staticXlsheader = [
      {
        header: 'ID',
        key: 'id',
        width: 20,
      },
      { header: 'Valor', key: 'value', width: 20 },
      {
        header: 'Status',
        key: 'status',
        width: 20,
      },
      {
        header: 'Data',
        key: 'createdAt',
        width: 20,
      },
      {
        header: 'Terminal',
        key: 'terminal',
        width: 20,
      },
      {
        header: 'Pagamento',
        key: 'payment',
        width: 20,
      },
    ];

    if (transportOperatorSelected.type === 1) {
      staticXlsheader.push({
        header: 'Operação',
        key: 'operator',
        width: 20,
      });
    }
    setXlsHeader(staticXlsheader);

    const rows = stableSort(
      paymentRequestList,
      getComparator('desc', 'createdAt'),
    ).map((data) => {
      const staticXlsRows = [
        {
          key: 'id',
          value: data?.id ? `${data?.id?.split('-')?.[0]}` : '-',
          image: null,
        },
        {
          key: 'value',
          value: data?.value
            ? `R$ ${data?.value?.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
              })}`
            : '-',
          image: null,
        },
        {
          key: 'status',
          value: data?.status
            ? `${
                CONST_PAYMENT_NEW.STATUS.find(({ KEY }) => KEY === data.status)
                  ?.VALUE
              }`
            : '-',
          image: null,
        },
        {
          key: 'createdAt',
          value: data?.createdAt
            ? `${new Date(data.createdAt).toLocaleString('pt-BR')}`
            : '-',
          image: null,
        },
        {
          key: 'terminal',
          value: data?.name ? `${data?.name}` : '-',
          image: null,
        },
        {
          key: 'payment',
          value: data?.type
            ? `${
                CONST_PAYMENT_NEW.TYPES.find(({ KEY }) => KEY === data.type)
                  ?.VALUE
              }`
            : '-',
          image: null,
        },
      ];

      if (transportOperatorSelected.type === 1) {
        staticXlsRows.push({
          key: 'operator',
          value: `${transportOperatorsList.find(
            (e) => e.id === data?.transportOperatorId,
          )?.name || '---'}`,
          image: null,
        });
      }
      return staticXlsRows;
    }) as XlsRowInterface[][];
    setXlsRows(rows);
  }, [paymentRequestList]);

  return (
    <div className={classes.root}>
      <MainContent className={classes.paperRoot}>
        <Grid ref={(ref: any) => setTableReference(ref)}>
          <Grid
            container
            justifyContent='space-between'
            className={classes.gridHeader}>
            <Grid item>
              <ReportTitle
                titleVariant='h4'
                title='Relatórios de Arrecadação • Requisição Pagamento'
                tooltipTitle={
                  TITLES_OPTIONS.REPORTS.COLLECTION.PAYMENT_REQUST.VALUE
                }
              />
            </Grid>
          </Grid>
          <Grid item spacing={2} className={classes.gridContent}>
            <Box
              display='flex'
              alignItems='center'
              className={classes.contentFilter}>
              <SearchComponent
                disabledInput={!contentIsVisible}
                data={initialPaymentRequestList}
                itemsByFilter={ITEMS.ReportPaymentRequest}
                results={(data: PaymentRequestInterface[]) =>
                  setPaymentRequestList(data)
                }
                classes={{
                  container: classes.search,
                }}
                modalFilter={() => {
                  dispatch(
                    setOpen({
                      open: true,
                      content: (
                        <Suspense fallback={<p>Carregando...</p>}>
                          <Filter
                            fields={ITEMS.paymentRequenst.remote}
                            handleApply={handleApply}
                          />
                        </Suspense>
                      ),
                      maxWidth: 'xl',
                    }),
                  );
                }}
              />
              {/* <Grid container justifyContent='flex-end'>
              <Button
                className={classes.addButton}
                onClick={() => setIsAddSubmissiion(true)}>
                <Typography style={{ marginRight: 8, fontSize: 20 }}>
                  +
                </Typography>{' '}
                <Typography style={{ fontSize: 12 }}>Programar Novo</Typography>
              </Button>
            </Grid> */}
            </Box>
            <Grid item>
              <CSVLink
                filename='Relatório_requisição_pagamento.csv'
                data={tableCSV}
                ref={csvRef}
              />
            </Grid>

            <Tabs
              indicatorColor='primary'
              onChange={handleChange}
              style={{
                background: '#ffffff',
                marginTop: 10,
                marginBottom: 32,
              }}
              textColor='primary'
              value={generalNavGuide}>
              <Tab label='Relatório' />
              <Tab label='Envios' />
              <Tab label='Histórico' />
            </Tabs>
            <TabPanel index={0} value={generalNavGuide}>
              {!contentIsVisible && (
                <ContentPlaceholder onClick={handleLoadData} />
              )}
              {contentIsVisible && (
                <Grid>
                  <Box
                    display='flex'
                    justifyContent='flex-end'
                    alignItems='center'>
                    {paymentRequestList?.length! > 0 && (
                      <Box
                        width='100%'
                        display='flex'
                        justifyContent='flex-end'
                        marginBottom='16px'>
                        <Suspense fallback='Carregando...'>
                          <TotalCountComponent
                            count={paymentRequestList?.length! || 0}
                            title='transações'
                            position='right'
                          />
                        </Suspense>
                      </Box>
                    )}

                    <Box display='flex' alignItems='center' marginBottom='16px'>
                      <DialogueExport
                        tableInstance={tableReference}
                        csvRef={csvRef}
                        simplePdf
                        simplePdfData={simplePdfData}
                        columnsPerPage={6}
                        pdfName='relatorio_de_arrecadacao_requisicao_pagamento'
                        xlsTitle='relatorio_de_arrecadacao_requisicao_pagamento'
                        xlsHeader={xlsHeader}
                        xlsRows={xlsRows}
                        pdf
                        csv
                        xls
                      />
                    </Box>
                  </Box>
                  <TablePaymentRequest
                    paymentRequestList={paymentRequestList}
                  />
                </Grid>
              )}
            </TabPanel>
            <TabPanel index={1} value={generalNavGuide}>
              <Submissions
                setSubmissionData={setSubmissionData}
                setIsEditSubmissiion={setIsEditSubmissiion}
                setIsAddSubmissiion={setIsAddSubmissiion}
                reportType={REPORTS_TYPES.SELLING_TERMINAL_PAYMENT_REQUEST}
              />
            </TabPanel>
            <TabPanel index={2} value={generalNavGuide}>
              <History
                reportType={REPORTS_TYPES.SELLING_TERMINAL_PAYMENT_REQUEST}
              />
            </TabPanel>
          </Grid>
        </Grid>
      </MainContent>
    </div>
  );
};

export { PaymentRequestDatails };
export default PaymentRequestDatails;
